<template>
  <div>
    <h2>Benchmarking</h2>
    <p>
      Das Benchmarking zeigt eine flächenbezogene Gegenüberstellung aller berechneten
      Maßnahmen und Maßnahmengruppen. Sämtliche Kennwerte werden auf die
      Bruttogrundfläche (m²) des Projekts normiert und ermöglichen so einen
      direkten Vergleich – sowohl innerhalb eines Projekts als auch
      projektübergreifend.
    </p>

    <h3>Kurz zum Start</h3>
    <ol>
      <li>Fläche in den Projektdetails hinterlegen — ohne sie bleibt alles 0.</li>
      <li>Kennwertkarten oben lesen: sie zeigen das Projekt je m².</li>
      <li>Tabelle darunter vergleicht die einzelnen Maßnahmen untereinander.</li>
    </ol>

    <h3>Abkürzungen und Einheiten</h3>
    <dl>
      <dt><strong>BGF</strong> (Bruttogrundfläche)</dt>
      <dd>Bezugsfläche aus den Projektdetails. Alle Kennwerte werden darauf normiert,
        damit sich verschieden große Liegenschaften vergleichen lassen.</dd>

      <dt><strong>kWh/(m²·a)</strong></dt>
      <dd>Energie je Quadratmeter und Jahr — der übliche Vergleichswert für Gebäude.</dd>

      <dt><strong>€/m²</strong> bzw. <strong>€/(m²·a)</strong></dt>
      <dd>Investition je Quadratmeter bzw. jährliche Kosten je Quadratmeter und Jahr.</dd>

      <dt><strong>kg CO₂e/(m²·a)</strong></dt>
      <dd>Treibhausgase als CO₂-Äquivalent: andere Gase werden in die Wirkung von CO₂
        umgerechnet und mitgezählt.</dd>
    </dl>

    <h3>Voraussetzungen</h3>
    <ul>
      <li>
        <strong>Bruttogrundfläche:</strong> In den Projektdetails muss eine gültige
        Fläche (m²) hinterlegt sein, damit flächenbezogene Kennwerte berechnet
        werden können. Ohne Flächenangabe werden alle Werte mit 0 angezeigt.
      </li>
      <li>
        <strong>Maßnahmen:</strong> Mindestens eine berechnete Maßnahme muss im
        Projekt vorhanden sein. Maßnahmen können auf der Seite
        <router-link
                v-if="projectId"
                :to="{ name: 'ProjectDashboard', params: { projectId: String(projectId) } }"
              >Maßnahmen</router-link><span v-else>Maßnahmen</span>
        ein- und ausgeschlossen werden.
      </li>
    </ul>

    <h3>Kennwerte-Übersicht (Karten)</h3>
    <p>
      Im oberen Bereich werden die wichtigsten Kennwerte als Zahlenkarten
      dargestellt:
    </p>
    <dl>
      <dt><strong>Euro-Kennwerte [€/m²]</strong></dt>
      <dd>
        Investitionskosten, jährliche Einsparung, Kapitalwert (NPV) und Annuität
        — jeweils normiert auf die Bruttogrundfläche.
      </dd>

      <dt><strong>Energie-Kennwerte [kWh/(m²·a)]</strong></dt>
      <dd>
        Primärenergie und Endenergie für Referenz- und Planungsfall sowie die
        resultierende Energieeinsparung pro m² und Jahr.
      </dd>

      <dt><strong>CO₂-Kennwerte [kg/(m²·a)]</strong></dt>
      <dd>
        CO₂-Emissionen für Referenz- und Planungsfall, CO₂-Einsparung sowie die
        dynamische Amortisationsdauer (Jahre) aus der VALERI-Berechnung.
      </dd>
    </dl>

    <h3>Gesamt-Benchmark-Diagramme</h3>
    <p>
      Drei Balkendiagramme stellen die aggregierten Kennwerte aller Maßnahmen
      als Gesamtwert dar:
    </p>
    <ul>
      <li><strong>Benchmark Euro [€/m²]</strong> — Investition, jährliche Einsparung, NPV, Annuität</li>
      <li><strong>Benchmark Energie [kWh/(m²·a)]</strong> — Primär-/Endenergie (Referenz &amp; Plan), Einsparung</li>
      <li><strong>Benchmark CO₂ [kg/(m²·a)]</strong> — CO₂ Referenz, CO₂ Plan, CO₂ Einsparung</li>
    </ul>

    <h3>Benchmark pro Maßnahme</h3>
    <p>
      Drei weitere Diagramme schlüsseln die Kennwerte nach einzelnen Maßnahmen
      auf. Jede Maßnahme bildet eine Gruppe auf der x-Achse; die farbigen Balken
      repräsentieren die jeweiligen Metriken. So ist sofort erkennbar, welche
      Maßnahme den größten Beitrag zu Einsparung, Investition oder
      CO₂-Reduktion leistet.
    </p>
    <ul>
      <li><strong>Euro pro Maßnahme</strong> — Investition, Einsparung, NPV, Annuität je Maßnahme</li>
      <li><strong>Energie pro Maßnahme</strong> — Endenergie Referenz/Plan und Einsparung je Maßnahme</li>
      <li><strong>CO₂ pro Maßnahme</strong> — CO₂ Referenz/Plan und Einsparung je Maßnahme</li>
    </ul>

    <h3>Benchmark pro Maßnahmengruppe</h3>
    <p>
      Sind im Projekt Maßnahmengruppen definiert, erscheint ein zusätzlicher
      Abschnitt mit drei Diagrammen, die die aggregierten Werte pro Gruppe
      darstellen. So können ganze Maßnahmenpakete miteinander verglichen werden.
    </p>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Datenexport &amp; Diagramme</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>Jedes Diagramm bietet über das Drei-Punkte-Menü (⋮) zwei Optionen:</p>
          <ul>
            <li>
              <strong>Grafik herunterladen</strong> — speichert das Diagramm als PNG-Bild.
            </li>
            <li>
              <strong>Daten exportieren</strong> — erzeugt eine Excel-kompatible
              Datei (.xls) mit den Chart-Daten zur Weiterverarbeitung.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Berechnungsgrundlage</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h3>Flächenbezug</h3>
          <p>
            Alle Kennwerte werden durch die Bruttogrundfläche (BGF) des Projekts
            geteilt. Dadurch sind Projekte unterschiedlicher Größe direkt
            vergleichbar.
          </p>

          <h3>Endenergie</h3>
          <p>
            Für die Endenergie-Berechnung werden die Energieflüsse der
            Energieträger-IDs 1–7 (Strom, Gas, Öl, Fernwärme etc.) summiert.
            Nur negative Mengen (Verbrauch) fließen ein; Erzeugungen werden
            nicht gegengerechnet.
          </p>

          <h3>Primärenergie</h3>
          <p>
            Die Primärenergie ergibt sich aus der Endenergie multipliziert mit
            dem jeweiligen Primärenergiefaktor des Energieträgers.
          </p>

          <h3>VALERI-Kennwerte</h3>
          <p>
            Kapitalwert (NPV), Annuität und dynamische Amortisationsdauer stammen
            aus der parallelen VALERI-Berechnung, die Diskontierung,
            Energiepreissteigerung und Nutzungsdauer berücksichtigt.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Hinweise</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ul>
            <li>
              Maßnahmen, die auf der Maßnahmen-Seite ausgeschlossen wurden, fließen
              nicht in die Benchmarking-Berechnung ein.
            </li>
            <li>
              Die Genauigkeit der Kennwerte hängt direkt von der Qualität der
              Eingabedaten in den einzelnen Maßnahmen ab. Bei überschlägigen
              Methoden ist eine Genauigkeit von ±15 % zu berücksichtigen.
            </li>
            <li>
              Ohne hinterlegte Bruttogrundfläche erscheint ein Warnhinweis und alle
              flächenbezogenen Werte werden als 0 dargestellt.
            </li>
            <li>
              Die Verknüpfungen zu Einsparanalyse, Energiebilanz und VALERI
              ermöglichen eine nahtlose Navigation zwischen den Auswertungsseiten.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-footer class="mt-10">
      <small>
        Das Fenster kann an der linken Kante angepasst und über „Hilfe" wieder
        geschlossen werden.
      </small>
    </v-footer>
  </div>
</template>

<script setup lang="ts">
/*
 * `projectId` aus dem Store, damit der Verweis auf die Maßnahmenseite
 * aufgelöst werden kann: die Route `ProjectDashboard` verlangt den Parameter
 * `projectId`. Ohne ihn blieb der `router-link` wirkungslos — derselbe Aufbau
 * wie in `ProjectCalcHelp.vue`.
 */
import { computed } from 'vue';
import { useProjectStore } from '@/stores/projectStore';

const store = useProjectStore();
const projectId = computed(() => store.currentProject?.id ?? null);
</script>

<style scoped></style>
